import { secondsInWeek } from 'date-fns/constants';
import { z } from 'zod';

import { configMetadata } from './metadata';
import type {
  AnyConfigSchema,
  ConfigKey,
  ConfigMetadata,
  ConfigSchemas,
  UIConfiguration,
} from './types';

/**
 * Constants for custom message and banner limits
 */
const CUSTOM_TITLE_LIMIT = 250;
const CUSTOM_MESSAGE_LIMIT = 10000;
const CUSTOM_BANNER_TEXT_LIMIT = 2000;

/**
 * Set the default log directory to `/var/log/anchore` in a production
 * environment, otherwise use the root of the local project directory
 */
const LOG_DIR_DEFAULT =
  // eslint-disable-next-line no-underscore-dangle
  process.env.NODE_ENV === 'development' || (global as any).__DEV__
    ? `${__dirname}/../..`
    : '/var/log/anchore';

/**
 * Pure Zod schemas for configuration validation. These schemas define the shape
 * and validation rules for each configuration item. Types are automatically
 * inferred from these schemas.
 */
export const configSchemas = {
  allowSharedLogin: z.boolean().default(true),

  apiClientUrl: z.string().min(1).default('/service'),

  apiServerUrl: z.string().min(1).default('/service'),

  appDbConfig: z
    .object({
      native: z.boolean().default(false),
      pool: z
        .object({
          min: z.number().int().min(0).default(0),
          max: z.number().int().min(1).default(10),
          acquire: z.number().int().min(1).default(30000),
          idle: z.number().int().min(1).default(10000),
        })
        .default({
          min: 0,
          max: 10,
          acquire: 30000,
          idle: 10000,
        }),
    })
    .default({
      native: false,
      pool: {
        min: 0,
        max: 10,
        acquire: 30000,
        idle: 10000,
      },
    }),

  appDbUri: z.url({ protocol: /^postgres|postgresql$/ }),

  authenticationLock: z
    .object({
      count: z.number().int().min(1),
      expires: z.number().int().min(1),
    })
    .nullable()
    .default(null),
  cacheExpiry: z
    .union([z.number().int().nonnegative(), z.literal(false)])
    .nullable()
    .default(600),

  configUIPath: z.string().min(1),

  customLinks: z
    .object({
      title: z.string().trim().min(1, {
        message:
          'The list of links requires a title that can be used as a label for the button or link that displays them',
      }),
      links: z
        .array(
          z.object({
            title: z
              .string()
              .trim()
              .min(1, { message: 'Link title is required' }),
            uri: z
              .string()
              .trim()
              .min(1, { message: 'Link URI is required' })
              .refine(
                value => {
                  // Allow relative paths starting with `/`
                  if (value.startsWith('/')) {
                    return true;
                  }

                  // Allow absolute URLs with safe protocols
                  try {
                    const url = new URL(value);
                    const safeProtocols = ['http:', 'https:'];
                    return safeProtocols.includes(url.protocol);
                  } catch {
                    // Invalid URL format
                    return false;
                  }
                },
                {
                  message:
                    'URI must be either a relative path (starting with /) or an absolute URL with http:// or https://',
                },
              ),
          }),
        )
        .max(10),
    })
    .nullable()
    .default(null),

  customMessage: z
    .object({
      title: z.string().trim().min(1).max(CUSTOM_TITLE_LIMIT),
      message: z.string().trim().min(1).max(CUSTOM_MESSAGE_LIMIT),
    })
    .nullable()
    .default(null),

  banners: z
    .object({
      top: z
        .object({
          text: z.string().trim().max(CUSTOM_BANNER_TEXT_LIMIT),
          display: z.enum(['always', 'login-only']).default('always'),
          background_color: z
            .string()
            .regex(
              /^(#[0-9A-Fa-f]{3,8}|rgb\([^)]*\)|rgba\([^)]*\)|hsl\([^)]*\)|[a-zA-Z]+)$/,
            )
            .optional(),
          text_color: z
            .string()
            .regex(
              /^(#[0-9A-Fa-f]{3,8}|rgb\([^)]*\)|rgba\([^)]*\)|hsl\([^)]*\)|[a-zA-Z]+)$/,
            )
            .optional(),
        })
        .nullable()
        .optional(),
      bottom: z
        .object({
          text: z.string().trim().max(CUSTOM_BANNER_TEXT_LIMIT),
          display: z.enum(['always', 'login-only']).default('always'),
          background_color: z
            .string()
            .regex(
              /^(#[0-9A-Fa-f]{3,8}|rgb\([^)]*\)|rgba\([^)]*\)|hsl\([^)]*\)|[a-zA-Z]+)$/,
            )
            .optional(),
          text_color: z
            .string()
            .regex(
              /^(#[0-9A-Fa-f]{3,8}|rgb\([^)]*\)|rgba\([^)]*\)|hsl\([^)]*\)|[a-zA-Z]+)$/,
            )
            .optional(),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .default(null)
    .superRefine((data, ctx) => {
      // Validate that text and display are set together for each banner
      if (!data || typeof data !== 'object' || Array.isArray(data)) return;

      const validateBanner = (
        banner:
          | {
              text?: string;
              display?: string;
              background_color?: string;
              text_color?: string;
            }
          | null
          | undefined,
        position: 'top' | 'bottom',
      ) => {
        if (!banner) return;

        const hasText = banner.text && banner.text.trim() !== '';
        const isDisplayEmpty = !banner.display || banner.display === '';
        const hasValidDisplay =
          banner.display === 'always' || banner.display === 'login-only';
        const hasBackgroundColor =
          banner.background_color && banner.background_color.trim() !== '';
        const hasTextColor =
          banner.text_color && banner.text_color.trim() !== '';

        // Check if ANY field is set (not just display)
        const hasAnyField =
          hasValidDisplay || hasBackgroundColor || hasTextColor;

        // Invalid: text exists but display is not set
        if (hasText && isDisplayEmpty) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Display setting is required when text is provided',
            path: [position, 'display'],
          });
        }
        // Invalid: any field is set but text is empty
        if (!hasText && hasAnyField) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Text is required when any banner settings are provided',
            path: [position, 'text'],
          });
        }
      };

      validateBanner(data.top, 'top');
      validateBanner(data.bottom, 'bottom');
    }),

  enableAddRepositories: z
    .object({
      admin: z.boolean().default(true),
      standard: z.boolean().default(true),
    })
    .default({ admin: true, standard: true }),

  enableHealthCheckRedirect: z.boolean().default(true),

  enablePrometheusMetrics: z.boolean().default(false),

  enableProxy: z.boolean().default(false),

  enableSSL: z.boolean().default(false),

  enrichInventoryView: z.boolean().default(true),

  enterpriseURI: z.url(),

  forceWebSocket: z.boolean().default(false),

  internalDeployment: z.boolean().default(false),

  ldapAuthConnectTimeout: z.number().int().positive().default(6000),

  ldapAuthTimeout: z.number().int().positive().default(6000),

  licensePath: z.string().min(1).default('/license.yaml'),

  logDir: z
    .string()
    .min(1)
    .regex(/^[a-zA-Z0-9_./\\-]+$/)
    .default(LOG_DIR_DEFAULT),

  logDirRetryInterval: z
    .number()
    .int()
    .min(
      60000,
      'Log directory retry interval must be at least 60000ms (60 seconds)',
    )
    .nullable()
    .default(60000),

  logFormat: z.enum(['text', 'json']).default('text'),

  logLevel: z
    .enum(['error', 'warn', 'info', 'http', 'debug', 'silly'])
    .default('http'),

  madRobotEnabled: z.boolean().default(false),

  madRobotRulesPath: z.string().min(1).optional(),

  port: z.number().int().min(1).max(65535).default(3000),

  redisClusterEnabled: z.boolean().default(false),

  redisFlushDb: z.boolean().default(true),

  redisUri: z
    .string()
    .min(1)
    .regex(/^(redis|rediss):\/\//),

  reportsUri: z
    .union([
      z.string().min(1), // Any non-empty string (including `false` to disable)
      z.literal(false), // Boolean false from YAML config file (`reports_uri: false`)
    ])
    .default(''),

  ssoAuthOnly: z.boolean().default(false),

  sessionTimeout: z
    .number()
    .int()
    .min(60, 'Session timeout must be at least 60 seconds')
    .default(secondsInWeek * 2), // 14 days in seconds, our current long-standing default
} as const;

/**
 * Create a combined schema for the entire configuration object. Most fields are
 * optional since they can have defaults or come from various sources.
 */
export const fullConfigSchema = z.object(configSchemas).partial();

/**
 * Helper to get a specific schema by key
 */
export const getSchema = <K extends ConfigKey>(key: K): ConfigSchemas[K] =>
  configSchemas[key];

/**
 * Extract the default value from a Zod schema if it exists. Returns `undefined`
 * if no default is defined. In our configuration system:
 * - Required configs (`isOptional: false`) *never* have defaults
 * - Optional configs (`isOptional: true`) *may* have defaults
 *
 * This function only handles optional schemas with defaults, as required schemas
 * should not have defaults by architectural design.
 *
 *  Note that this implementation uses Zod's official `safeParse` API rather than
 * internal properties, making it more stable across Zod versions (as the `_def`
 * property is not part of the public API and is deprecated).
 */
export const extractDefaultFromSchema = <T = unknown>(
  schema: AnyConfigSchema,
): T | undefined => {
  try {
    // Only check for optional schemas with defaults. This works for schemas
    // like: `z.boolean().default(true).optional()`
    const undefinedResult = schema.safeParse(undefined);
    if (undefinedResult.success && undefinedResult.data !== undefined) {
      return undefinedResult.data as T;
    }

    // No default value found
    return undefined;
  } catch {
    return undefined;
  }
};

/**
 * Get the default value for a specific configuration key. Returns `undefined`
 * if no default is defined.
 */
export const getDefaultValue = <K extends ConfigKey>(
  key: K,
): UIConfiguration[K] | undefined => {
  const schema = configSchemas[key];
  return extractDefaultFromSchema<UIConfiguration[K]>(schema);
};

/**
 * Check if a configuration key has a default value.
 */
export const hasDefaultValue = (key: ConfigKey): boolean => {
  const defaultValue = getDefaultValue(key);
  return defaultValue !== undefined;
};

/**
 * Extract enum values from a Zod schema if it's an enum type. Returns
 * `undefined` if the schema is not an enum.
 *
 * This function uses Zod's public `.options` property when available,
 * which is much simpler than accessing internal `_def` structures.
 */
export const getEnumValues = <K extends ConfigKey>(
  key: K,
): string[] | undefined => {
  const schema = configSchemas[key];

  // Check if schema is a ZodEnum instance by checking for options property
  if (schema && 'options' in schema && Array.isArray(schema.options)) {
    return schema.options as string[];
  }

  // For schemas wrapped with .default(), check the inner type
  if (schema && typeof schema === 'object' && '_def' in schema) {
    // eslint-disable-next-line no-underscore-dangle
    const def = (schema as any)._def;

    // Check for ZodDefault wrapping a ZodEnum
    if (def.type === 'default' && def.innerType && 'options' in def.innerType) {
      return def.innerType.options as string[];
    }

    // Check for `ZodOptional` is wrapping a `ZodEnum`
    if (
      def.type === 'optional' &&
      def.innerType &&
      'options' in def.innerType
    ) {
      return def.innerType.options as string[];
    }

    // Check for `ZodOptional(ZodDefault(ZodEnum))`
    // eslint-disable-next-line no-underscore-dangle
    if (def.type === 'optional' && def.innerType && def.innerType._def) {
      // eslint-disable-next-line no-underscore-dangle
      const innerDef = def.innerType._def;
      if (
        innerDef.type === 'default' &&
        innerDef.innerType &&
        'options' in innerDef.innerType
      ) {
        return innerDef.innerType.options as string[];
      }
    }
  }

  return undefined;
};

/**
 * Determine the configuration type from metadata. This function returns the
 * explicitly defined type from the config metadata, ensuring consistent type
 * information between server and client.
 */
export const getConfigType = <K extends ConfigKey>(
  key: K,
): ConfigMetadata['type'] => {
  const metadata = configMetadata[key];

  if (!metadata || !metadata.type) {
    // Fallback to 'string' if metadata is missing (shouldn't happen in practice)
    return 'string';
  }

  return metadata.type;
};

/**
 * Check if a configuration schema accepts null values. Returns true for schemas
 * defined with `.nullable()` or `.optional()`.
 */
export const isNullable = <K extends ConfigKey>(key: K): boolean => {
  const schema = configSchemas[key];
  return schema.safeParse(null).success;
};
